import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import ru from './locales/ru.json';

export const SUPPORTED_LOCALES = ['en', 'ru'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const LOCALE_STORAGE_KEY = 'app-locale';

/**
 * Site default locale — matches the pre-rendered index.html lang="ru".
 *
 * The i18n instance MUST initialize with this locale on both server AND the
 * client's first render. vite-ssg pre-renders the marketing routes as 'ru';
 * if the client's first render used a different locale (e.g. 'en' from
 * navigator.language) the rendered text would differ from the static HTML,
 * Vue would fail to hydrate and re-render the whole tree, replaying every
 * entrance animation. The browser/localStorage preference is resolved AFTER
 * mount via resolveStoredLocale(), which switches the reactive locale post-
 * hydration (a plain text patch, not a node replacement).
 */
const DEFAULT_LOCALE: SupportedLocale = 'ru';

/**
 * Resolve the visitor's preferred locale from the browser.
 * Priority:
 * 1. localStorage (an explicit choice made via the language toggle)
 * 2. Telegram only: browser language, else 'en'. This is just an interim
 *    value until the user's Telegram language_code arrives from /users/me
 *    (applied in App.vue), so Telegram behaviour stays as before.
 * 3. Web: no override — keep the site default ('ru'). Web visitors arrive from
 *    the Russian-only marketing site, so an English browser locale must not
 *    flip the app flow to English mid-funnel (/rasklady → /reading/...).
 *
 * Browser-only — must NOT be called during SSR/SSG or the client's first
 * render. Returns `null` when the default locale should be left untouched.
 */
export function resolveStoredLocale({ isTelegram }: { isTelegram: boolean }): SupportedLocale | null {
  if (typeof window === 'undefined') return null;

  // 1. Persisted explicit choice
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved && SUPPORTED_LOCALES.includes(saved as SupportedLocale)) {
      return saved as SupportedLocale;
    }
  } catch {
    // localStorage not available
  }

  // 3. Web: stay on the Russian site default
  if (!isTelegram) return null;

  // 2. Telegram: browser language until the backend language_code loads
  const browserLang = navigator.language.split('-')[0];
  if (SUPPORTED_LOCALES.includes(browserLang as SupportedLocale)) {
    return browserLang as SupportedLocale;
  }
  return 'en';
}

/**
 * Save locale to localStorage for persistence across refreshes.
 */
export function saveLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // localStorage not available
  }
}

export const i18n = createI18n({
  legacy: false, // Use Composition API
  globalInjection: true, // Make $t and locale changes reactive globally
  // Always start on the site default so the client's first render matches the
  // pre-rendered 'ru' HTML and Vue can hydrate in place. The visitor's actual
  // preference is applied after mount (see App.vue → resolveStoredLocale()).
  locale: DEFAULT_LOCALE,
  fallbackLocale: 'en',
  messages: { en, ru },
});
